import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  TextField,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from '@mui/material';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { useBarcodeLookup } from '../hooks/useItemSearch';
import { colors } from '../constants';

/**
 * Web barcode scanner. Uses @zxing/browser to read from the device camera and
 * looks the code up as a product or ingredient. Falls back to manual entry when
 * a camera is unavailable or denied.
 */
export default function BarcodeScannerModal({
  open,
  onClose,
  onItemFound,
  onBarcodeNotFound,
}) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const readerRef = useRef(null);
  const barcodeLookup = useBarcodeLookup();
  const [processing, setProcessing] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [manualCode, setManualCode] = useState('');
  // When several items share the scanned code we can't auto-pick one, so we
  // freeze the scanner and render these for the user to choose from.
  const [matches, setMatches] = useState([]);
  const lastCodeRef = useRef(null);

  const toFoundItem = (item) => ({
    uuid: item.uuid || item.id,
    sku: item.sku,
    name: item.name,
    barcodes: item.barcodes || item.barcodeNumber || [],
    stock: item.stock,
    unit: item.unit,
    unitPrice: item.unitPrice,
    isActive: item.isActive,
    categoryId: item.categoryId ?? null,
    type: item.type,
  });

  const pickMatch = (item) => {
    onItemFound(toFoundItem(item));
    handleClose();
  };

  const handleCode = async (code) => {
    if (!code || processing || lastCodeRef.current === code) return;
    lastCodeRef.current = code;
    setProcessing(true);
    try {
      const result = await barcodeLookup.mutateAsync(code);
      if (!result.found || result.items.length === 0) {
        onBarcodeNotFound(code);
        handleClose();
      } else if (result.items.length === 1) {
        onItemFound(toFoundItem(result.items[0]));
        handleClose();
      } else {
        // Multiple items share this barcode: stop scanning and let the user
        // choose which one to add rather than guessing.
        stopCamera();
        setMatches(result.items);
      }
    } catch {
      onBarcodeNotFound(code);
      handleClose();
    } finally {
      setProcessing(false);
      lastCodeRef.current = null;
    }
  };

  const stopCamera = () => {
    try {
      controlsRef.current?.stop();
    } catch {
      /* noop */
    }
    controlsRef.current = null;
  };

  const handleClose = () => {
    stopCamera();
    setManualCode('');
    setCameraError(null);
    setMatches([]);
    onClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    let controls = null;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    // ZXing logs a NotFound/Checksum/Format "exception" for every video frame
    // that has no decodable barcode, which floods the console during a scan.
    // These are expected scan-loop misses, not errors, so silence just those
    // lines while the scanner is open and restore the console on teardown.
    const originalLog = console.log;
    const originalWarn = console.warn;
    const isZxingScanNoise = (args) =>
      typeof args[0] === 'string' && args[0].includes('MultiFormatReader');
    console.log = (...args) => {
      if (isZxingScanNoise(args)) return;
      originalLog.apply(console, args);
    };
    console.warn = (...args) => {
      if (isZxingScanNoise(args)) return;
      originalWarn.apply(console, args);
    };

    const start = async () => {
      // The dialog content (and thus the <video>) is mounted through a MUI
      // portal/transition, so it may not be in the DOM on the first tick after
      // `open` flips true. Wait for the element before starting the camera,
      // otherwise ZXing throws "no video element" and the camera never opens.
      for (let i = 0; i < 40 && active && !videoRef.current; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      if (!active || !videoRef.current) return;

      try {
        controls = await reader.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (result) => {
            if (result) handleCode(result.getText());
          }
        );
        // If the effect was torn down while the camera was starting, stop the
        // stream this invocation created so it doesn't leak or kill a newer one.
        if (!active) {
          controls.stop();
          controls = null;
          return;
        }
        controlsRef.current = controls;
      } catch (e) {
        if (active) setCameraError(e?.message || 'Camera unavailable');
      }
    };

    start();

    return () => {
      active = false;
      try {
        controls?.stop();
      } catch {
        /* noop */
      }
      controls = null;
      controlsRef.current = null;
      console.log = originalLog;
      console.warn = originalWarn;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {matches.length > 0 ? 'Select an item' : 'Scan Barcode'}
      </DialogTitle>
      <DialogContent>
        {matches.length > 0 ? (
          <>
            <Typography variant="body2" sx={{ color: colors.gray600, mb: 1 }}>
              Multiple items share this barcode. Choose the one to add:
            </Typography>
            <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {matches.map((item) => (
                <ListItemButton
                  key={item.uuid || item.id || item.sku}
                  onClick={() => pickMatch(item)}
                  sx={{ borderRadius: 1, mb: 0.5, border: `1px solid ${colors.border}` }}
                >
                  <ListItemText
                    primary={item.name}
                    secondary={`SKU: ${item.sku || '—'}${
                      item.unit ? ` · ${item.unit}` : ''
                    }`}
                  />
                  <Chip
                    size="small"
                    label={item.type === 'ingredient' ? 'Ingredient' : 'Product'}
                    sx={{ ml: 1 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        ) : cameraError ? (
          <Typography variant="body2" sx={{ color: colors.error, mb: 2 }}>
            {cameraError}. Enter the barcode manually below.
          </Typography>
        ) : (
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: '#000',
              aspectRatio: '4 / 3',
              mb: 2,
            }}
          >
            <video
              ref={videoRef}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              muted
              playsInline
            />
            {processing && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.4)',
                }}
              >
                <CircularProgress sx={{ color: colors.white }} />
              </Box>
            )}
          </Box>
        )}

        {matches.length === 0 && (
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              label="Enter barcode manually"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && manualCode.trim()) handleCode(manualCode.trim());
              }}
            />
            <Button
              variant="contained"
              disabled={!manualCode.trim() || processing}
              onClick={() => handleCode(manualCode.trim())}
              sx={{ backgroundColor: colors.primary }}
            >
              Look up
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
